import { useState } from 'react';
import { Play, Info, CheckCircle } from 'lucide-react';
import { safeInvoke } from '../../lib/safeInvoke';
import { useToast } from '../../components/ui/ToastSystem';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';

interface CronJob {
  id: string;
  name: string;
  schedule: string;
  description: string;
}

const CRON_JOBS: CronJob[] = [
  { id: 'alert-system',             name: 'Sistema de Alertas',          schedule: 'A cada 15 min',  description: 'Verifica thresholds e dispara alertas críticos' },
  { id: 'calculate-daily-transits', name: 'Trânsitos Diários',           schedule: '00:01 UTC',      description: 'Calcula posições planetárias do dia' },
  { id: 'batch-processor',          name: 'Processador em Lote',         schedule: '03:30 UTC',      description: 'Processa tarefas agendadas em fila' },
  { id: 'generate-daily-insights',  name: 'Insights Diários',            schedule: '04:00 UTC',      description: 'Gera previsões personalizadas em background' },
  { id: 'send-daily-notifications', name: 'Notificações Diárias',        schedule: '05:00 UTC',      description: 'Envia push + e-mail de previsão do dia' },
  { id: 'send-renewal-reminders',   name: 'Lembretes de Renovação',      schedule: '08:00 BRT',      description: 'Avisa assinantes com renovação em 7 dias' },
  { id: 'weekly-forecast-digest',   name: 'Resumo Semanal',              schedule: 'Segunda 09:00',  description: 'Envia previsão semanal por e-mail' },
  { id: 'send-daily-compliance',    name: 'Relatório de Compliance',     schedule: 'Diário 07:00',   description: 'Verifica conformidade de fornecedores' },
  { id: 'process-failed-webhooks',  name: 'Reprocessar Webhooks Falhos', schedule: 'A cada hora',    description: 'Retenta webhooks Stripe que falharam' },
  { id: 'send-admin-daily-report',  name: 'Relatório Admin WhatsApp',    schedule: '11:00 UTC (08h BRT)', description: 'Envia resumo diário de métricas para o WhatsApp do administrador' },
  { id: 'process-email-sequences', name: 'Sequências de Email',          schedule: '09:00 UTC',           description: 'Processa filas de onboarding, winback e reengajamento' },
];

export function AdminCronJobsPage() {
  const { showToast } = useToast();
  const [running, setRunning] = useState<Record<string, boolean>>({});

  async function triggerJob(job: CronJob) {
    setRunning(prev => ({ ...prev, [job.id]: true }));
    const result = await safeInvoke(job.id, { fallback: null, timeout: 30_000 });
    setRunning(prev => ({ ...prev, [job.id]: false }));

    if (result.fromFallback || result.error) {
      showToast({
        type: 'error',
        message: `Erro ao executar "${job.name}": ${result.error ?? 'função não respondeu'}`,
      });
    } else {
      showToast({
        type: 'success',
        message: `"${job.name}" executado com sucesso!`,
      });
    }
  }

  return (
    <div>
      <AdminPageHeader
        title="Tarefas Agendadas (Cron)"
        objective="Visualize e execute manualmente as tarefas automáticas gerenciadas pelo pg_cron no Supabase. O status 'Ativo' indica que a tarefa está cadastrada e será executada no horário configurado."
      />

      {/* Info card */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
        padding: '0.75rem 1rem',
        backgroundColor: 'rgba(96,165,250,0.08)',
        border: '1px solid rgba(96,165,250,0.2)',
        borderRadius: 'var(--radius-sm)',
        marginBottom: '1.5rem',
        fontSize: 'var(--text-caption)',
        color: 'var(--text-secondary)',
      }}>
        <Info size={15} style={{ flexShrink: 0, color: '#60a5fa', marginTop: '1px' }} />
        <span>
          Estas tarefas são executadas automaticamente via <strong style={{ color: 'var(--text-primary)' }}>pg_cron</strong> no Supabase.
          Use o botão <strong style={{ color: 'var(--text-primary)' }}>Executar agora</strong> para forçar execução manual de qualquer tarefa sem aguardar o próximo ciclo agendado.
        </span>
      </div>

      {/* Jobs table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="data-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Nome</th>
              <th>Agendamento</th>
              <th>Descrição</th>
              <th style={{ textAlign: 'center' }}>Status</th>
              <th style={{ textAlign: 'center' }}>Ação</th>
            </tr>
          </thead>
          <tbody>
            {CRON_JOBS.map(job => (
              <tr key={job.id}>
                <td>
                  <p style={{ color: 'var(--text-primary)', fontWeight: 600, margin: 0, fontSize: '13px' }}>{job.name}</p>
                  <p style={{ color: 'var(--text-muted)', fontSize: '11px', fontFamily: 'monospace', margin: 0 }}>{job.id}</p>
                </td>
                <td>
                  <span style={{ fontSize: '12px', padding: '2px 8px', borderRadius: '999px', background: 'rgba(31,78,95,0.1)', color: 'var(--petrol)', whiteSpace: 'nowrap' }}>
                    {job.schedule}
                  </span>
                </td>
                <td style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>{job.description}</td>
                <td style={{ textAlign: 'center' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '12px', color: '#4ade80', fontWeight: 600 }}>
                    <CheckCircle size={13} /> Ativo
                  </span>
                </td>
                <td style={{ textAlign: 'center' }}>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => triggerJob(job)}
                    disabled={running[job.id]}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '12px', whiteSpace: 'nowrap' }}
                  >
                    <Play size={11} />
                    {running[job.id] ? 'Executando...' : 'Executar agora'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
